import React, { useEffect, useState, useContext } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
    ShieldCheckIcon, ArrowDownTrayIcon, TrashIcon, KeyIcon,
    MapPinIcon, BriefcaseIcon, LockClosedIcon, EnvelopeIcon,
    SparklesIcon, PencilSquareIcon, CheckIcon, XMarkIcon, CalendarDaysIcon,
    ChevronDownIcon
} from '@heroicons/react/24/outline';

const DEPARTMENTS = ["General", "IT", "Sales", "HR", "Finance", "Marketing", "Management", "Development", "QA/QC"];

const HR_QUOTES = [
    { text: "To win in the marketplace you must first win in the workplace.", author: "Doug Conant" },
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Human Resources isn't a thing we do. It's the thing that runs our business.", author: "Steve Wynn" }
];

const Profile = () => {
    const { user } = useContext(AuthContext);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quote] = useState(HR_QUOTES[Math.floor(Math.random() * HR_QUOTES.length)]);

    // Admin Edit States
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    // Password States
    const [passData, setPassData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passLoading, setPassLoading] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const res = await api.get('/auth/profile');
            setProfile(res.data);
            setEditData({
                full_name: res.data.full_name,
                department: res.data.department || 'General',
                role: res.data.role,
                created_at: new Date(res.data.created_at).toISOString().split('T')[0]
            });
            setLoading(false);
        } catch (err) {
            toast.error("Failed to load profile");
        }
    };

    const handleUpdateProfile = async () => {
        setUpdateLoading(true);
        try {
            // URL must match your router (e.g., /auth/update-profile)
            await api.put('/auth/update-profile', editData);
            toast.success("Profile Updated Successfully");
            setIsEditing(false);
            fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.error || "Update failed");
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passData.newPassword !== passData.confirmPassword) return toast.error("New passwords do not match");
        setPassLoading(true);
        try {
            await api.put('/auth/change-password', passData);
            toast.success("Password Updated");
            setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.error || "Update Failed");
        } finally {
            setPassLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "Never";
        return new Date(dateString).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) return (
        <Layout><div className="animate-pulse max-w-7xl mx-auto px-4 mt-10"><div className="h-64 bg-slate-200 rounded-3xl"></div></div></Layout>
    );

    return (
        <Layout>
            {/* HERO SECTION */}
            <div className="mb-12 max-w-7xl mx-auto">
                <div className="min-h-[18rem] bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-3xl overflow-hidden relative shadow-2xl flex items-center justify-center p-8 group">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="relative z-10 text-center max-w-3xl">
                        <SparklesIcon className="h-8 w-8 text-indigo-400 mx-auto mb-4" />
                        <blockquote className="text-xl md:text-3xl font-serif italic text-white/90 leading-relaxed">"{quote.text}"</blockquote>
                        <div className="mt-6 flex items-center justify-center space-x-3">
                            <div className="h-px w-8 bg-indigo-500/50"></div>
                            <span className="text-indigo-200 text-xs font-bold uppercase tracking-[0.2em]">{quote.author}</span>
                            <div className="h-px w-8 bg-indigo-500/50"></div>
                        </div>
                    </div>
                </div>

                <div className="px-6 md:px-12 flex flex-col md:flex-row items-center md:items-start relative z-10 -mt-20">
                    <div className="w-32 h-32 md:w-40 md:h-40 bg-white p-1.5 rounded-full shadow-2xl ring-4 ring-slate-50">
                        <div className="w-full h-full rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-5xl font-black">
                            {profile.full_name.charAt(0)}
                        </div>
                    </div>
                    <div className="mt-24 md:mt-24 md:ml-6 text-center md:text-left flex-1">
                        <h1 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tight">{profile.full_name}</h1>
                        <div className="mt-3 flex flex-wrap justify-center md:justify-start gap-2">
                            <span className="px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold">{profile.role}</span>
                            <span className="px-4 py-1.5 rounded-full bg-white border border-slate-200 text-slate-500 text-xs font-medium">{profile.email}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {/* LEFT: EMPLOYMENT DETAILS */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                            <div className="flex items-center">
                                <div className="p-2.5 bg-indigo-50 rounded-xl mr-3"><BriefcaseIcon className="h-5 w-5 text-indigo-600" /></div>
                                <h3 className="font-bold text-lg text-slate-800">Employment Record</h3>
                            </div>
                            {(user.role === 'Admin' || user.role === 'Super Admin') && (
                                !isEditing ? (
                                    <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full text-xs font-bold hover:bg-indigo-700 shadow-md">
                                        <PencilSquareIcon className="w-4 h-4" /> Edit Record
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button onClick={() => setIsEditing(false)} className="p-2 text-slate-400 hover:text-slate-600"><XMarkIcon className="w-6 h-6" /></button>
                                        <button onClick={handleUpdateProfile} disabled={updateLoading} className="flex items-center gap-2 px-5 py-2 bg-emerald-500 text-white rounded-full text-xs font-bold hover:bg-emerald-600 shadow-lg disabled:opacity-50">
                                            {updateLoading ? 'Saving...' : <><CheckIcon className="w-4 h-4" /> Save</>}
                                        </button>
                                    </div>
                                )
                            )}
                        </div>

                        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                            {/* Department Dropdown */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Department</label>
                                {isEditing ? (
                                    <div className="relative">
                                        <select
                                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold appearance-none outline-none focus:ring-2 focus:ring-indigo-500/20"
                                            value={editData.department}
                                            onChange={e => setEditData({ ...editData, department: e.target.value })}
                                        >
                                            {DEPARTMENTS.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                                        </select>
                                        <ChevronDownIcon className="w-4 h-4 absolute right-3 top-3.5 text-slate-400 pointer-events-none" />
                                    </div>
                                ) : (
                                    <div className="text-slate-800 font-bold text-lg border-l-2 border-indigo-500 pl-4">{profile.department || 'General'}</div>
                                )}
                            </div>

                            {/* Role Select */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Designation</label>
                                {isEditing ? (
                                    <select
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
                                        value={editData.role}
                                        onChange={e => setEditData({ ...editData, role: e.target.value })}
                                    >
                                        <option value="Employee">Employee</option>
                                        <option value="Manager">Manager</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Super Admin">Super Admin</option>
                                    </select>
                                ) : (
                                    <div className="text-slate-800 font-bold text-lg border-l-2 border-slate-100 pl-4">{profile.role}</div>
                                )}
                            </div>

                            {/* Joined Date Picker */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Joined On</label>
                                {isEditing ? (
                                    <div className="relative">
                                        <CalendarDaysIcon className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                                        <input
                                            type="date" className="w-full pl-10 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
                                            value={editData.created_at}
                                            onChange={e => setEditData({ ...editData, created_at: e.target.value })}
                                        />
                                    </div>
                                ) : (
                                    <div className="text-slate-800 font-bold text-lg border-l-2 border-slate-100 pl-4">
                                        {new Date(profile.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Region</label>
                                <div className="inline-flex items-center bg-slate-50 text-slate-600 px-4 py-1.5 rounded-full text-sm font-bold border">
                                    <MapPinIcon className="w-4 h-4 mr-2" /> {profile.country || 'India'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PRIVACY CARD */}
                    <div className="bg-white rounded-3xl p-8 border shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex gap-4 items-center">
                            <div className="p-3 bg-emerald-50 rounded-2xl"><ShieldCheckIcon className="h-7 w-7 text-emerald-600" /></div>
                            <div>
                                <h3 className="font-bold text-slate-800">Compliance & Data</h3>
                                <p className="text-sm text-slate-400">GDPR Compliant. You can request data porting anytime.</p>
                            </div>
                        </div>
                        <button className="w-full md:w-auto px-8 py-3 bg-slate-900 text-white rounded-2xl text-sm font-bold hover:shadow-xl transition-all">Export JSON</button>
                    </div>
                </div>

                {/* RIGHT: SECURITY */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden sticky top-8">
                        <div className="h-1.5 bg-gradient-to-r from-indigo-500 to-purple-600"></div>
                        <div className="p-6 border-b flex items-center">
                            <KeyIcon className="h-5 w-5 text-slate-400 mr-2" />
                            <h3 className="font-bold text-slate-800">Security Center</h3>
                        </div>
                        <form onSubmit={handleChangePassword} className="p-6 space-y-5">
                            <div className="relative">
                                <LockClosedIcon className="w-5 h-5 absolute left-3 top-3.5 text-slate-300" />
                                <input
                                    type="password" required className="w-full pl-10 pr-4 py-3.5 bg-slate-50 border rounded-2xl text-sm outline-none focus:bg-white focus:ring-4 focus:ring-indigo-500/5"
                                    placeholder="Current Password" value={passData.currentPassword}
                                    onChange={e => setPassData({ ...passData, currentPassword: e.target.value })}
                                />
                            </div>
                            <input
                                type="password" required className="w-full px-4 py-3.5 bg-slate-50 border rounded-2xl text-sm outline-none focus:bg-white focus:ring-4 focus:ring-indigo-500/5"
                                placeholder="New Password" value={passData.newPassword}
                                onChange={e => setPassData({ ...passData, newPassword: e.target.value })}
                            />
                            <input
                                type="password" required className="w-full px-4 py-3.5 bg-slate-50 border rounded-2xl text-sm outline-none focus:bg-white focus:ring-4 focus:ring-indigo-500/5"
                                placeholder="Confirm New Password" value={passData.confirmPassword}
                                onChange={e => setPassData({ ...passData, confirmPassword: e.target.value })}
                            />
                            <button disabled={passLoading} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold text-sm shadow-xl hover:bg-indigo-600 transition-all active:scale-[0.98]">
                                {passLoading ? 'Verifying...' : 'Update Password'}
                            </button>
                        </form>
                        <div className="bg-slate-50/50 p-4 border-t text-center">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                Password changed: <span className="text-indigo-600">{formatDateTime(profile.password_changed_at)}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Profile;
